import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ButtonDropdown, DangerText, TDropdownElementProps } from 'ui-toolkit';
import { FeeSectionTableColumn } from './table/FeeSectionTableColumn';
import { ITableColumn } from './table/getColumns';
import { StyledTableNumberColumn, StyledTableRow } from './table/StyledTable';
import { ILineItem } from './ILineItem';
import { MergeDocumentModal } from '../documentsPortal/standardDocuments/MergeDocumentModal';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/types';
import { Form, Formik } from 'formik';
import { PDFPreview } from 'ui-toolkit/components/PDFPreview';
import { createDownloadDocumentUrl } from 'API/order/documents/adminDocumentsAPI';
import { IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import { IFeeSection } from './organizeFees';

interface IFeeContainerProps {
  columns: ITableColumn[];
  fee: ILineItem;
  index?: number;
  onDelete?: () => void;
  isEditable?: boolean;
  editUrl: string;
  onPrint: (documents: string[]) => any;
  feeSection: IFeeSection;
}

const StyledIcon = styled(FontAwesomeIcon)`
  padding-left: ${(props) => props.theme.spacing.roomyContentPaddingValue}px;
  padding-right: ${(props) => props.theme.spacing.roomyContentPaddingValue}px;
`;

export const FeeContainer: React.FC<IFeeContainerProps> = ({
  editUrl,
  onDelete,
  isEditable,
  columns,
  index,
  fee,
  onPrint,
  feeSection,
}) => {
  const hasDocuments = (fee.documents?.length ?? 0) > 0;
  const kabobItems: TDropdownElementProps[] = useMemo(() => {
    const result = [];
    if (isEditable) {
      result.push({ children: 'Edit Item', to: editUrl, 'data-test-id': `edit-${fee.id}` });
    } else {
      result.push({ children: 'View Item', to: editUrl, 'data-test-id': `view-${fee.id}` });
      result.push({ children: 'Cancel', onClick: () => {}, 'data-test-id': `cancel-${fee.id}` });
    }
    if (hasDocuments) {
      result.push({
        children: 'Print',
        onClick: () => {
          setShowModal(true);
        },
        'data-test-id': `print-${fee.id}`,
      });
    }
    if (onDelete) {
      result.push({ children: <DangerText>Delete</DangerText>, onClick: onDelete, 'data-test-id': `delete-${fee.id}` });
    }
    return result;
  }, [isEditable, editUrl, onDelete, fee.id, hasDocuments]);
  const [showModal, setShowModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<IStandardDocumentVersionData | null>(null);
  const onClose = () => {
    setShowModal(false);
  };
  const mergeDocumentsFetchStatus = useSelector(
    (state: RootStore) => state.order.documents.adminDocuments.mergeDocumentsFetchStatus
  );
  return (
    <>
      <StyledTableRow className={`items-baseline`}>
        <StyledTableNumberColumn>
          {index !== undefined && (index + 1).toString().padStart(2, '0')}
        </StyledTableNumberColumn>
        {columns.map((column) => (
          <FeeSectionTableColumn
            key={`${fee.id}-${column.label}`}
            columnValue={column.value(
              fee.stringSettlementStatementPropertyValues,
              fee.numericSettlementStatementPropertyValues
            )}
            columnName={column.label}
            columnVisible={column.isVisible}
            editUrl={editUrl}
            sectionDefinitionId={feeSection.sectionDefinition.statementSectionDefinitionId}
            fee={fee}
          />
        ))}
        <div className={`self-center justify-self-center`}>
          {kabobItems.length > 0 && (
            <ButtonDropdown intent={'ghost'} isInline items={kabobItems} data-test-id={`${fee.id}-kabob`}>
              <StyledIcon icon={faEllipsisV} />
            </ButtonDropdown>
          )}
        </div>
      </StyledTableRow>
      {showModal && (
        <Formik
          initialValues={{
            selectedDocuments: fee.documents ?? [],
            category: fee.documents?.at(0)?.category,
          }}
          onSubmit={async (values) => {
            const result = await onPrint(values.selectedDocuments.map((d) => d.id));
            setShowModal(false);
            setPreviewDoc({
              id: result['data'].standardDocumentId,
              versionId: result['data'].id,
            } as IStandardDocumentVersionData);
          }}
        >
          {() => (
            <Form>
              <MergeDocumentModal
                fetchError={'There was an error combining documents.'}
                fetchStatus={mergeDocumentsFetchStatus}
                onClose={onClose}
              />
            </Form>
          )}
        </Formik>
      )}
      {previewDoc && (
        <PDFPreview
          file={createDownloadDocumentUrl(previewDoc.id, previewDoc.versionId)}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </>
  );
};
